import {
  applicationStepSelected,
  fetchCityStateFromZip,
  fetchRedemptionTypes,
  fetchZipcodeFromIP,
  stepSelected,
  updateLead,
} from 'common/actions'
import {
  applicationFieldBlurred,
  applicationFieldChanged,
  applicationFieldRegistered,
  applicationFieldShowValidationStatus,
  applicationFieldUnregistered,
  applicationUpdateValidationStatus,
} from 'common/actions/application'
import {
  ApplicationStep,
  Field,
  FieldData,
  FieldDataValue,
  FieldKey,
  FieldType,
  StepbystepApplicationStep,
} from 'embed/constants'
import { creditScoreOfferUrlSelector, validationDataSelector } from 'embed/selectors'
import {
  applicationCreditCardConsentSelector,
  applicationCreditCardConsentsToFCRAFieldSelector,
  applicationCreditCardConsentsToTCPAFieldSelector,
  applicationCreditCardFieldsSelector,
  applicationCreditCardPurposesFieldSelector,
} from 'embed/selectors/application'
import {
  configCreditCardRewardsEnabledSelector,
  configShouldDisplayBackToSearchSelector,
  configStepSelector,
} from 'embed/selectors/config'
import { Fields } from 'embed/constants/fields'
import { MultiProductState } from 'embed/reducers'
import { uiHasCalledGeocoderSelector, uiSelectedCreditApplicationStepSelector } from 'embed/selectors/ui'
import { CreditCardPurposesOptions, CreditCardStep, Product } from 'even-ts-static'
import { every } from 'lodash'
import * as React from 'react'
import { connect } from 'react-redux'
import {
  stepByStepCreditApplicationSteps,
  stepByStepEarnRewardsCreditApplicationSteps,
  stepByStepNoRedemptionCreditApplicationSteps,
} from './application-steps'
import { StepbystepApplicationStepAddress } from './Default/address'
import { StepbystepApplicationStepAnnualIncome } from './Default/annual-income'
import { StepbystepApplicationStepDateOfBirth } from './Default/date-of-birth'
import { StepbystepApplicationStepEmail } from './Default/email'
import { StepbystepApplicationStepName } from './Default/name'
import { StepbystepApplicationStepCreditRedemptionTypes } from './Rewards/redemption-types'
import { StepbystepApplicationStepCreditRewardsPurposes } from './Rewards/reward-card-purposes'

interface DispatchProps {
  dispatchApplicationFieldBlurred: (field: Field) => void
  dispatchApplicationFieldChanged: (field: Field, value: FieldDataValue) => void
  dispatchApplicationFieldRegistered: (field: Field) => void
  dispatchApplicationFieldUnregistered: (field: Field) => void
  dispatchApplicationStepSelected: (applicationStep: ApplicationStep) => void
  dispatchApplicationFieldShowValidationStatus: (fieldKey: FieldKey, show: boolean) => void
  dispatchApplicationUpdateValidationStatus: () => void
  dispatchBackToIntro: () => void
  dispatchBackToStepOne: () => void
  dispatchConsentsToFCRAandTCPA: () => void
  dispatchFetchCityStateFromZip: (zipcode: string) => void
  dispatchFetchRedemptionTypes: () => void
  dispatchFetchZipcodeFromIP: () => void
  dispatchUpdateLead: () => void
}

interface OwnProps {
  collectFinancialInformation: boolean
}

interface StoreProps {
  configuredForIntroStep: boolean
  consent: boolean
  consentValidationStatus: boolean
  creditScoreOfferUrl?: string
  directReferral: boolean
  hasCalledGeocoder: boolean
  rewardsEnabled: boolean
  selectedCardPurpose: FieldDataValue | undefined
  shouldDisplayBackToSearch: boolean
  stepByStepApplicationStep: ApplicationStep | undefined
  stepByStepApplicationStepIsValid: boolean
}

type Props = StoreProps & DispatchProps & OwnProps

const getApplicationSteps = (rewardsEnabled: boolean, selectedCardPurpose: FieldDataValue): ApplicationStep[] => {
  let steps
  if (rewardsEnabled) {
    if (selectedCardPurpose === CreditCardPurposesOptions.EARNING_REWARDS) {
      steps = stepByStepEarnRewardsCreditApplicationSteps
    } else {
      steps = stepByStepNoRedemptionCreditApplicationSteps
    }
  } else {
    steps = stepByStepCreditApplicationSteps
  }

  return steps
}

const getNextStep = (stepValues: ApplicationStep[], stepByStepApplicationStep?: ApplicationStep) => {
  const step = stepValues.indexOf(
    stepByStepApplicationStep ?? StepbystepApplicationStep.CreditCardRewardsGroupsButtonList,
  )
  return stepValues[step + 1]
}

const shouldGoToNextStep = (isStepValid: boolean, skipValidate?: boolean): boolean =>
  (!skipValidate && isStepValid) || !!skipValidate

const shouldUnregisterRedemptionTypesFields = (currentCardPurpose: FieldDataValue): boolean =>
  currentCardPurpose === CreditCardPurposesOptions.LOW_INTEREST ||
  currentCardPurpose === CreditCardPurposesOptions.NEW_TO_CREDIT

const applicationFieldsUnregister = (currentCardPurpose, dispatchApplicationFieldUnregistered) => {
  if (shouldUnregisterRedemptionTypesFields(currentCardPurpose)) {
    dispatchApplicationFieldUnregistered(Fields.RedemptionTypesButtonList)
  }
}

const StepByStepCreditApplicationContainer: React.FC<Props> = ({
  configuredForIntroStep,
  creditScoreOfferUrl,
  directReferral,
  dispatchApplicationFieldChanged,
  dispatchApplicationFieldUnregistered,
  dispatchApplicationFieldShowValidationStatus,
  dispatchApplicationStepSelected,
  dispatchBackToIntro,
  dispatchBackToStepOne,
  dispatchConsentsToFCRAandTCPA,
  dispatchFetchRedemptionTypes,
  dispatchUpdateLead,
  rewardsEnabled,
  selectedCardPurpose,
  shouldDisplayBackToSearch,
  stepByStepApplicationStep,
  stepByStepApplicationStepIsValid,
}) => {
  const [currentStep, setCurrentStep] = React.useState(1)
  const [totalNumberOfSteps, setTotalNumberOfSteps] = React.useState(0)
  const currentCardPurpose = React.useRef(selectedCardPurpose)
  const delay = 200

  React.useEffect(() => {
    setTotalNumberOfSteps(getApplicationSteps(rewardsEnabled, currentCardPurpose.current).length)
    dispatchUpdateLead()
    dispatchApplicationStepSelected(getApplicationSteps(rewardsEnabled, currentCardPurpose.current)[0])
  }, [])

  React.useEffect(() => {
    if (totalNumberOfSteps !== getApplicationSteps(rewardsEnabled, currentCardPurpose.current).length) {
      setTimeout(() => {
        setTotalNumberOfSteps(getApplicationSteps(rewardsEnabled, currentCardPurpose.current).length)
      }, delay)
    }
  }, [rewardsEnabled, selectedCardPurpose, totalNumberOfSteps])

  const onContinue = React.useCallback(
    (skipValidate?: boolean) => {
      const stepValues: ApplicationStep[] = getApplicationSteps(rewardsEnabled, currentCardPurpose.current)
      const nextStep = getNextStep(stepValues, stepByStepApplicationStep)
      const nextStepIndex = stepValues.indexOf(nextStep)

      if (shouldGoToNextStep(stepByStepApplicationStepIsValid, skipValidate)) {
        setCurrentStep(nextStepIndex + 1)
        dispatchApplicationStepSelected(nextStep)
        return
      }
      // Show which fields are invalid for this step
      if (stepByStepApplicationStep !== undefined) {
        Object.values(FieldsForStepValidation[stepByStepApplicationStep]).forEach((fieldKey: FieldKey) => {
          dispatchApplicationFieldShowValidationStatus(fieldKey, true)
        })
      }
    },
    [
      dispatchApplicationFieldShowValidationStatus,
      dispatchApplicationStepSelected,
      rewardsEnabled,
      setCurrentStep,
      stepByStepApplicationStep,
      stepByStepApplicationStepIsValid,
    ],
  )

  const onRewardsGroupContinue = React.useCallback(() => {
    applicationFieldsUnregister(currentCardPurpose.current, dispatchApplicationFieldUnregistered)
    onContinue(true)
  }, [dispatchApplicationFieldUnregistered, onContinue])

  const onBack = React.useCallback(() => {
    if (stepByStepApplicationStep !== undefined) {
      const stepValues: ApplicationStep[] = getApplicationSteps(rewardsEnabled, currentCardPurpose.current)
      const step = stepValues.indexOf(stepByStepApplicationStep)
      const previousStep = stepValues[step - 1]
      const previousStepIndex = stepValues.indexOf(previousStep)
      setCurrentStep(previousStepIndex + 1)
      dispatchApplicationStepSelected(previousStep)
    }
  }, [dispatchApplicationStepSelected, rewardsEnabled, stepByStepApplicationStep])

  const onChange = React.useCallback((field: Field, value) => dispatchApplicationFieldChanged(field, value), [
    dispatchApplicationFieldChanged,
  ])

  const handleChangeAndContinue = React.useCallback(
    (field: Field, value: string) => {
      onChange(field, value)
      if (field.type !== FieldType.ButtonList) {
        return
      }
      let continueFunction = () => onContinue(true)
      if (stepByStepApplicationStep === StepbystepApplicationStep.CreditCardRewardsGroupsButtonList) {
        currentCardPurpose.current = value
        continueFunction = onRewardsGroupContinue
      }
      // Delay continuing slightly, so you briefly see the active state of the button
      setTimeout(continueFunction, delay)
    },
    [onChange, onContinue, onRewardsGroupContinue, stepByStepApplicationStep],
  )

  const handleReturnToOriginalStep = configuredForIntroStep ? dispatchBackToIntro : dispatchBackToStepOne

  const isLastStep = currentStep === totalNumberOfSteps
  const onEnter = !isLastStep ? onContinue : undefined

  const applicationStepProps = {
    creditScoreOfferUrl,
    currentStep,
    directReferral,
    dispatchReturnToOriginalStep: handleReturnToOriginalStep,
    handleChangeAndContinue,
    handleConsentsToFCRAandTCPA: dispatchConsentsToFCRAandTCPA,
    onBack,
    onContinue,
    onEnter,
    shouldDisplayBackToSearch,
    stepByStepApplicationStep,
    stepByStepApplicationStepIsValid,
    totalNumberOfSteps,
  }
  const rewardsPreferenceTypes = {
    fetchRedemptionTypes: dispatchFetchRedemptionTypes,
  }

  switch (stepByStepApplicationStep) {
    case StepbystepApplicationStep.CreditCardRewardsGroupsButtonList:
      return <StepbystepApplicationStepCreditRewardsPurposes {...applicationStepProps} />
    case StepbystepApplicationStep.CreditCardRedemptionTypes:
      return <StepbystepApplicationStepCreditRedemptionTypes {...applicationStepProps} {...rewardsPreferenceTypes} />
    case StepbystepApplicationStep.Name:
      return <StepbystepApplicationStepName {...applicationStepProps} />
    case StepbystepApplicationStep.Email:
      return <StepbystepApplicationStepEmail {...applicationStepProps} />
    case StepbystepApplicationStep.DateOfBirth:
      return <StepbystepApplicationStepDateOfBirth {...applicationStepProps} />
    case StepbystepApplicationStep.Address:
      return <StepbystepApplicationStepAddress {...applicationStepProps} />
    case StepbystepApplicationStep.AnnualIncome:
      return <StepbystepApplicationStepAnnualIncome {...applicationStepProps} />
    default:
      return <></>
  }
}

const FieldsForStepValidation = {
  [StepbystepApplicationStep.CreditCardRewardsGroupsButtonList]: [Fields.CreditCardRewardsGroupsButtonList.key],
  [StepbystepApplicationStep.CreditCardRedemptionTypes]: [Fields.RedemptionTypesButtonList.key],
  [StepbystepApplicationStep.Email]: [Fields.EmailAddress.key],
  [StepbystepApplicationStep.DateOfBirth]: [Fields.DateOfBirth.key],
  [StepbystepApplicationStep.Name]: [Fields.FirstName.key, Fields.LastName.key],
  [StepbystepApplicationStep.Address]: [
    Fields.AddressOne.key,
    Fields.AddressTwo.key,
    Fields.Zipcode.key,
    Fields.City.key,
    Fields.State.key,
  ],
  [StepbystepApplicationStep.AnnualIncome]: [Fields.AnnualIncomeCredit.key],
  [StepbystepApplicationStep.Consent]: [Fields.ConsentsToFCRA.key, Fields.ConsentsToTCPA.key],
}

export const mapStateToProps = (state: MultiProductState): StoreProps => {
  const consent = applicationCreditCardConsentSelector(state) ?? false
  const configStep = configStepSelector(state)

  const consentValidationStatus = !!(
    applicationCreditCardConsentsToFCRAFieldSelector(state)?.shouldShowValidationStatus &&
    applicationCreditCardConsentsToTCPAFieldSelector(state)?.shouldShowValidationStatus
  )

  const configuredForIntroStep = configStep === CreditCardStep.Intro
  const creditScoreOfferUrl = creditScoreOfferUrlSelector(state)
  const directReferral = configStep === CreditCardStep.RemarketingForm
  const hasCalledGeocoder = uiHasCalledGeocoderSelector(state)
  const rewardsEnabled = configCreditCardRewardsEnabledSelector(state)
  const selectedCardPurpose = applicationCreditCardPurposesFieldSelector(state)?.value
  const shouldDisplayBackToSearch = configShouldDisplayBackToSearchSelector(state)
  const stepByStepApplicationStep = uiSelectedCreditApplicationStepSelector(state)
  const validationData = validationDataSelector(state)

  const stepByStepApplicationStepIsValid = every(
    applicationCreditCardFieldsSelector(state) ?? {},
    (fieldData: FieldData, fieldKey: FieldKey) => {
      const field = Object.values(Fields).find((el) => el.key === fieldKey)
      const value = fieldData.value
      return stepByStepApplicationStep !== undefined &&
        Object.values(FieldsForStepValidation[stepByStepApplicationStep] ?? {}).includes(fieldKey)
        ? field !== undefined && field.validate(value, validationData)
        : true
    },
  )

  return {
    configuredForIntroStep,
    consent,
    consentValidationStatus,
    creditScoreOfferUrl,
    directReferral,
    hasCalledGeocoder,
    rewardsEnabled,
    selectedCardPurpose,
    shouldDisplayBackToSearch,
    stepByStepApplicationStep,
    stepByStepApplicationStepIsValid,
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchApplicationFieldBlurred: (field: Field) => dispatch(applicationFieldBlurred(Product.CreditCard, field.key)),
  dispatchApplicationFieldChanged: (field: Field, value: number | string) =>
    dispatch(applicationFieldChanged(Product.CreditCard, field.key, value)),
  dispatchApplicationFieldRegistered: (field: Field) =>
    dispatch(applicationFieldRegistered(Product.CreditCard, field.key)),
  dispatchApplicationFieldShowValidationStatus: (fieldKey: FieldKey, show: boolean) =>
    dispatch(applicationFieldShowValidationStatus(Product.CreditCard, fieldKey, show)),
  dispatchApplicationFieldUnregistered: (field: Field) =>
    dispatch(applicationFieldUnregistered(Product.CreditCard, field.key)),
  dispatchApplicationStepSelected: (applicationStep: StepbystepApplicationStep) =>
    dispatch(applicationStepSelected(Product.CreditCard, applicationStep)),
  dispatchApplicationUpdateValidationStatus: () => dispatch(applicationUpdateValidationStatus(Product.CreditCard)),
  dispatchBackToIntro: () => dispatch(stepSelected(Product.CreditCard, CreditCardStep.Intro)),
  dispatchBackToStepOne: () => dispatch(stepSelected(Product.CreditCard, CreditCardStep.Preview)),
  dispatchConsentsToFCRAandTCPA: () => {
    dispatch(applicationFieldChanged(Product.CreditCard, Fields.ConsentsToFCRA.key, true))
    dispatch(applicationFieldBlurred(Product.CreditCard, Fields.ConsentsToFCRA.key))
    dispatch(applicationFieldChanged(Product.CreditCard, Fields.ConsentsToTCPA.key, true))
    dispatch(applicationFieldBlurred(Product.CreditCard, Fields.ConsentsToTCPA.key))
  },
  dispatchFetchCityStateFromZip: (zipcode) => dispatch(fetchCityStateFromZip(Product.CreditCard, zipcode)),
  dispatchFetchRedemptionTypes: () => dispatch(fetchRedemptionTypes()),
  dispatchFetchZipcodeFromIP: () => dispatch(fetchZipcodeFromIP(Product.CreditCard)),
  dispatchUpdateLead: () => dispatch(updateLead(Product.CreditCard)),
})

export const CreditStepByStepContainer = connect<StoreProps, DispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(StepByStepCreditApplicationContainer)
